import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect } from 'react';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { useRenameChat } from '../hooks/useRenameChat';
import { Chat } from '../types';

interface RenameModalProps {
  chat: Chat;
  onClose: () => void;
}

const RenameModal: React.FC<RenameModalProps> = ({ chat, onClose }) => {
  const { renameChat, isRenaming } = useRenameChat();
  const [renameValue, setRenameValue] = useState(chat.title);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 100);
  }, []);

  const handleFinishRename = async () => {
    if (renameValue.trim() && !isRenaming) {
      try {
        await renameChat({ chatId: chat.id, title: renameValue.trim() });
        onClose();
      } catch (error) {
        console.error('Rename failed:', error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFinishRename();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div
        className='fixed inset-0 bg-black bg-opacity-50 transition-opacity'
        onClick={onClose}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
        role='button'
        tabIndex={0}
        aria-label='Close modal'
      />

      <div className='relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-medium text-gray-900'>Rename Chat</h3>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors duration-200'
            aria-label='Close modal'
          >
            <XMarkIcon className='w-5 h-5' />
          </button>
        </div>

        <div className='mb-6'>
          <label
            htmlFor='chat-name'
            className='block text-sm font-medium text-gray-700 mb-2'
          >
            Chat Name
          </label>
          <input
            id='chat-name'
            ref={renameInputRef}
            type='text'
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            placeholder='Enter chat name...'
            maxLength={100}
          />
          <div className='mt-1 text-xs text-gray-500'>
            {renameValue.length}/100 characters
          </div>
        </div>

        <div className='flex justify-end space-x-3'>
          <button
            onClick={onClose}
            disabled={isRenaming}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 rounded-lg transition-colors duration-200'
          >
            Cancel
          </button>
          <button
            onClick={handleFinishRename}
            disabled={!renameValue.trim() || isRenaming}
            className='px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors duration-200 flex items-center'
          >
            {isRenaming && (
              <LoadingSpinner color='border-white' className='mr-2' />
            )}
            {isRenaming ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenameModal;
